import type { Role } from '@/features/auth/api';

/**
 * 操作マニュアル（/help）のトピック定義。
 * 各画面をトピックにまとめ、URLパスからトピックIDを解決できるようにする。
 * これにより PageHeader が現在地に応じた「使い方」リンクを自動で出せる
 * （個別のページ側でリンク先を指定する必要がない）。
 */
export interface HelpTopic {
  id: string;
  /** マニュアル内の見出し */
  label: string;
  /** このトピックを主に使うロール（表示の目安。実際の可否はAPI側の認可が正） */
  roles: Role[];
}

export const HELP_TOPICS: HelpTopic[] = [
  {
    id: 'dashboard',
    label: 'ホーム（ダッシュボード）',
    roles: ['student', 'director', 'success', 'office', 'super_admin'],
  },
  { id: 'members', label: '会員（受講生）', roles: ['student', 'director', 'success', 'office'] },
  {
    id: 'profile',
    label: '自分の顔写真・連絡先・パスワード',
    roles: ['student', 'director', 'success', 'office', 'super_admin'],
  },
  { id: 'portfolio', label: '年間ポートフォリオ', roles: ['student', 'director', 'success', 'office'] },
  { id: 'events', label: 'イベント（月例会・分科会・面談など）', roles: ['student', 'director', 'success', 'office'] },
  // 理事・講師は D-295 で順位一覧を閉じたため対象外
  { id: 'scores', label: '採点・順位', roles: ['student', 'office'] },
  { id: 'office-accounts', label: '事務局のログインアカウント', roles: ['office'] },
  { id: 'staff', label: '理事・講師／サクセス担当', roles: ['office'] },
  { id: 'masters', label: '業務マスター（選択肢の登録）', roles: ['office'] },
  {
    id: 'notification-setup',
    label: '自動配信メールの設定（文面とタイミング）',
    roles: ['office'],
  },
  { id: 'availability', label: '実施可能日（面談の空き）', roles: ['success'] },
  {
    // 理事・講師／サクセス担当にはこの画面を出していない（D-295。お知らせはメールで届く）
    id: 'notifications',
    label: 'システムからのお知らせ（メールなど）',
    roles: ['student', 'office'],
  },
  { id: 'president', label: '社長アンケート', roles: ['student', 'director', 'office'] },
  { id: 'surveys', label: '課題・アンケート', roles: ['student', 'office'] },
  { id: 'messages', label: 'メッセージ（やり取り）', roles: ['student', 'director', 'success', 'office'] },
  { id: 'inbox', label: '問い合わせ対応', roles: ['office'] },
  { id: 'success', label: 'サクセス面談の記録', roles: ['success', 'office'] },
  { id: 'integrations', label: '外部サービスとのつなぎ', roles: ['office', 'super_admin'] },
  { id: 'audit-logs', label: '操作の記録（監査ログ）', roles: ['office', 'super_admin'] },
  { id: 'superadmin', label: 'システム運営会社向けの画面', roles: ['super_admin'] },
];

/**
 * パス（ルールが長い順に評価）→ トピックID。
 * `/masters/event-types` のように特定ルールが汎用ルール（`/masters`）より
 * 先に評価される必要があるため、実行時にプレフィックス長で降順ソートして判定する。
 */
const PATH_RULES: [prefix: string, topicId: string][] = [
  ['/masters/event-types', 'masters'],
  ['/masters/survey-templates', 'masters'],
  // 自動配信メールは覚えることが多いので、業務マスターとは別の専用ページに送る
  ['/masters/notification-presets', 'notification-setup'],
  ['/masters/notification-templates', 'notification-setup'],
  ['/masters/ng-rules', 'masters'],
  ['/masters/cohorts', 'masters'],
  ['/masters/courses', 'masters'],
  ['/directors', 'staff'],
  ['/success-staff', 'staff'],
  ['/masters', 'masters'],
  ['/surveys/president/import', 'president'],
  ['/president', 'president'],
  ['/scores/manage', 'scores'],
  ['/scores', 'scores'],
  ['/office-accounts', 'office-accounts'],
  ['/notifications/schedules', 'notifications'],
  ['/notifications', 'notifications'],
  ['/inbox', 'inbox'],
  ['/messages', 'messages'],
  ['/follow-ups', 'success'],
  ['/availability', 'availability'],
  ['/portfolio', 'portfolio'],
  ['/superadmin/integrations', 'integrations'],
  ['/superadmin', 'superadmin'],
  ['/integrations', 'integrations'],
  ['/audit-logs', 'audit-logs'],
  ['/events', 'events'],
  ['/members', 'members'],
  ['/profile', 'profile'],
  ['/account', 'profile'],
];

const SORTED_RULES = [...PATH_RULES].sort((a, b) => b[0].length - a[0].length);

/** 現在のパス名からヘルプのトピックIDを解決する。該当なしは null（ログイン画面など）。 */
export function topicForPath(pathname: string): string | null {
  if (pathname === '/') return 'dashboard';
  if (/^\/members\/[^/]+\/portfolio/.test(pathname)) return 'portfolio';
  // 会員配下だが内容は社長アンケート（D-315）。/members より先に判定する
  if (/^\/members\/[^/]+\/president-survey/.test(pathname)) return 'president';
  for (const [prefix, topicId] of SORTED_RULES) {
    if (pathname === prefix || pathname.startsWith(`${prefix}/`)) {
      return topicId;
    }
  }
  return null;
}

export function labelForTopic(topicId: string): string | null {
  return HELP_TOPICS.find((t) => t.id === topicId)?.label ?? null;
}
